import { MessageCircle, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMe, useProduct } from "../api/hooks";
import { BrandHeader } from "../components/BrandHeader";
import { Faq } from "../components/Faq";
import { ContentIcon } from "../components/Icons";
import { PersonalBlock } from "../components/PersonalBlock";
import { ProductShot } from "../components/ProductShot";
import { faq, fallbackProduct, home, legal } from "../content/site";
import { formatMoney } from "../lib/money";
import { useSupport } from "../lib/support";
import { haptic } from "../lib/telegram";
import { currentTheme, toggleTheme } from "../lib/theme";

/**
 * Главная — карточка товара (экран 8 ТЗ): product shot, формат и цена,
 * «Купить» и консультация, преимущества, персональный блок, FAQ и нижний CTA.
 * Корзины нет: «Купить» ведёт сразу в оформление с выбранным вариантом.
 */
export function HomeScreen() {
  const navigate = useNavigate();
  const productQuery = useProduct();
  const me = useMe();
  const support = useSupport();
  const product = productQuery.data ?? fallbackProduct;
  const [theme, setTheme] = useState(currentTheme);

  const variant =
    product.variants.find((v) => v.id === product.defaultVariantId) ?? product.variants[0];
  const available = Boolean(variant?.available);

  const buy = () => {
    if (!variant || !available) return;
    haptic("medium");
    navigate(`/checkout?variant=${encodeURIComponent(variant.id)}&qty=1`);
  };

  return (
    <div className="screen">
      <div className="screen__header">
        <BrandHeader />
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme(toggleTheme())}
          aria-label={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
        >
          {theme === "dark" ? <Sun size={18} strokeWidth={1.9} aria-hidden /> : <Moon size={18} strokeWidth={1.9} aria-hidden />}
        </button>
      </div>

      <section className="product">
        <ProductShot product={product} size="hero" />
        <h1 className="product__title">{product.title || home.title}</h1>
        <div className="product__format">
          {variant?.label}
          {product.subtitle ? ` · ${product.subtitle}` : ""}
        </div>
        <div className="product__price">{variant ? formatMoney(variant.price) : "—"}</div>

        <div className="stack" style={{ marginTop: 14 }}>
          <button type="button" className="button" onClick={buy} disabled={!available}>
            {available ? home.buy : home.soldOut}
          </button>
          <button type="button" className="button button--ghost" onClick={support.open} disabled={!support.available}>
            <MessageCircle size={17} strokeWidth={1.9} aria-hidden />
            {home.consult}
          </button>
        </div>
      </section>

      <section className="block">
        <h2 className="block__title">{home.benefitsTitle}</h2>
        <div className="benefits">
          {home.benefits.map((benefit) => (
            <div key={benefit.title} className="benefit">
              <span className="benefit__icon">
                <ContentIcon name={benefit.icon} size={18} />
              </span>
              <span>{benefit.title}</span>
            </div>
          ))}
        </div>
      </section>

      <PersonalBlock me={me.data} />

      <section className="block">
        <h2 className="block__title">{home.faqTitle}</h2>
        <Faq items={faq} defaultOpen={0} />
      </section>

      <section className="hero" style={{ marginTop: 28 }}>
        <h2 className="hero__title" style={{ fontSize: 22 }}>{home.bottomCta.title}</h2>
        <p className="hero__body">{home.bottomCta.body}</p>
        <button type="button" className="button button--light" style={{ marginTop: 16 }} onClick={support.open} disabled={!support.available}>
          {home.bottomCta.button}
        </button>
      </section>

      <div className="legal">
        <p>{legal.disclaimer}</p>
        <p style={{ marginTop: 8 }}>{legal.copyright}</p>
      </div>
    </div>
  );
}
